import React from 'react';
import { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import { AuthAPI } from '../../../api';
import { AuthStore } from '../../../store';
import FormControl from '@material-ui/core/FormControl';
import Button from '../../../components/Button';
import { TextInput } from './Inputs';
import AlertList from './AlertList';
import Typography from '@material-ui/core/Typography';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import {useSelector, useDispatch} from 'react-redux';
import {
  validateFirstName,
  validateLastName,
  validatePhone,
  validateNdisNumber,
} from './Validate';
import { LocationInput } from '../../../components/input';
import moment from 'moment';
import MomentUtils from '@date-io/moment';
import {
  MuiPickersUtilsProvider,
  DatePicker,
} from '@material-ui/pickers';
import * as track from '../../../lib/track';


const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    flexWrap: 'wrap',
    padding: '1rem 0',
  },
  control: {
    width: '25rem',
    marginBottom: '2rem',
  },
  label: {
    fontSize: '1.3rem',
    marginBottom: '0.7rem',
    color: theme.palette.text.primary,
    '&:focus': {
      color: theme.palette.text.primary,
    },
  },
  row: {
    width: '25rem',
    marginBottom: '1rem',
  },
}));

const ParticipantDetailsForm = ({isAdmin=false, setProfile=null}) => {
  const classes = useStyles();
  const [alerts, setAlerts] = useState([]);
  const profile = useSelector(state => state.auth.profile);
  const currentUuid = useSelector(state => state.auth.profile.user_uuid);
  const dispatch = useDispatch();
  const [firstName, setFirstName] = useState(profile.first_name);
  const [lastName, setLastName] = useState(profile.last_name);
  const [phone, setPhone] = useState(profile.phone);
  const [location, setLocation] = useState(profile.location);
  const [ndisNumber, setNdisNumber] = useState(profile.ndis_number);
  const [ndisPlanType, setNdisPlanType] = useState(profile.ndis_plan_type);
  const [ndisPlanStart, setNdisPlanStart] = useState(profile.ndis_plan_start);
  const [dateOfBirth, setDateOfBirth] = useState(profile.date_of_birth);
  const [firstNameError, setFirstNameError] = useState();
  const [lastNameError, setLastNameError] = useState();
  const [phoneError, setPhoneError] = useState();
  const [ndisNumberError, setNdisNumberError] = useState();
  const [busy, setBusy] = useState(false);
  const planTypes = ['Self-Managed', 'Plan Managed', 'Agency Managed'];
  const apiUpdateProfile = isAdmin ? AuthAPI.adminUpdateProfile : AuthAPI.updateProfile;
  const errors =
    firstNameError !== undefined ||
    lastNameError !== undefined ||
    phoneError !== undefined ||
    ndisNumberError !== undefined;

  // // if the profile gets updated, update the form.
  useEffect(() => setFirstName(profile.first_name), [profile.first_name]);
  useEffect(() => setLastName(profile.last_name), [profile.last_name]);
  useEffect(() => setPhone(profile.phone), [profile.phone]);
  useEffect(() => setLocation(profile.location), [profile.location]);
  useEffect(() => setNdisNumber(profile.ndis_number), [profile.ndis_number]);
  useEffect(() => setNdisPlanType(profile.ndis_plan_type), [profile.ndis_plan_type]);
  useEffect(() => setNdisPlanStart(profile.ndis_plan_start), [profile.ndis_plan_start]);
  useEffect(() => setDateOfBirth(profile.date_of_birth), [profile.date_of_birth]);

  const addAlerts = (newAlerts) => {
    setAlerts(alerts => [...newAlerts, ...alerts]);
  };

  const removeAlert = (alert) => {
    setAlerts(alerts => alerts.filter(a => a !== alert));
  };

  const firstNameValidator = (changing=true) => e => {
    const value = e.target.value;
    if (changing) setFirstName(value);
    if (!changing || firstNameError) setFirstNameError(validateFirstName(value));
  };

  const lastNameValidator = (changing=true) => e => {
    const value = e.target.value;
    if (changing) setLastName(value);
    if (!changing || lastNameError) setLastNameError(validateLastName(value));
  };

  const phoneValidator = (changing=true) => e => {
    const value = e.target.value;
    if (changing) setPhone(value);
    if (!changing || phoneError) setPhoneError(validatePhone(value));
  };

  const ndisNumberValidator = (changing=true) => e => {
    const value = e.target.value;
    if (changing) setNdisNumber(value);
    if (!changing || ndisNumberError) setNdisNumberError(validateNdisNumber(value));
  };

  const onSave = async () => {

    track.sendEvent('save participant details', 'click', {});

    // compose the arguments to be sent
    const updates = {};
    if (firstName !== '' && firstName !== profile.first_name) {
      updates.first_name = firstName;
    }
    if (lastName !== '' && lastName !== profile.last_name) {
      updates.last_name = lastName;
    }
    if (phone !== '' && phone !== profile.phone) {
      updates.phone = phone;
    }
    if (location !== null && location !== profile.location) {
      updates.location = location;
    }
    if (dateOfBirth !== null && dateOfBirth !== profile.date_of_birth) {
      updates.date_of_birth = dateOfBirth;
    }
    if (ndisNumber !== '' && ndisNumber !== profile.ndis_number) {
      updates.ndis_number = ndisNumber;
    }
    if (ndisPlanType !== '' && ndisPlanType !== profile.ndis_plan_type) {
      updates.ndis_plan_type = ndisPlanType;
    }
    if (ndisPlanStart !== null && ndisPlanStart !== profile.ndis_plan_start) {
      updates.ndis_plan_start = ndisPlanStart;
    }
    setAlerts([]);

    // if there is nothing to update, say so and abort
    if (Object.keys(updates).length === 0) {
      addAlerts([{severity: 'info', text: 'No changes to save.'}]);
      return;
    }

    // send update to the API
    setBusy(true);
    updates.uuid = profile.user_uuid;  // required for admin updates, ignored otherwise
    try {
      const response = await apiUpdateProfile(updates);
      addAlerts(response.payload.messages);
      if (profile.user_uuid === currentUuid) {
        // if isAdmin, may or may not be profile of current user.
        dispatch(AuthStore.profile(response.payload.profile));
      }
      if (setProfile) {
        setProfile(response.payload.profile);
      }
    } catch (err) {
      if (err.body) {
        const { message } = err.body;
        addAlerts([{severity: 'error', text: message}]);
      } else {
        addAlerts([{severity: 'error', text: 'An error occurred updating your profile.'}]);
      }
    } finally {
      setBusy(false);
    }

  };

  return (
    <div className={classes.root}>

      <FormControl className={classes.control}>
        <Typography variant="subtitle2" gutterBottom>
          First name
        </Typography>
        {firstNameError !== undefined ? (
          <Typography variant="body2" color="error" gutterBottom>
            {firstNameError}
          </Typography>
        ) : null}
        <TextInput
          id="first-name-input"
          value={firstName}
          variant="outlined"
          placeholder="First name"
          error={firstNameError !== undefined}
          onChange={firstNameValidator(true)}
          onBlur={firstNameValidator(false)}
        />
      </FormControl>

      <FormControl className={classes.control}>
        <Typography variant="subtitle2" gutterBottom>
          Last name
        </Typography>
        {lastNameError !== undefined ? (
          <Typography variant="body2" color="error" gutterBottom>
            {lastNameError}
          </Typography>
        ) : null}
        <TextInput
          id="last-name-input"
          value={lastName}
          variant="outlined"
          placeholder="Last name"
          error={firstNameError !== undefined}
          onChange={lastNameValidator(true)}
          onBlur={lastNameValidator(false)}
        />
      </FormControl>

      <FormControl className={classes.control}>
        <Typography variant="subtitle2" gutterBottom>
          Phone number
        </Typography>
        <Typography variant="body2" gutterBottom>
          Providing your phone number allows us to contact you quickly if needed, for example if you have requested a service and we need additional information from you.
        </Typography>
        <Typography variant="body2" gutterBottom>
          Providing a mobile number is best as we plan to provide SMS notifications of important events in the near future.
        </Typography>
        {phoneError !== undefined ? (
          <Typography variant="body2" color="error" gutterBottom>
            {phoneError}
          </Typography>
        ) : null}
        <TextInput
          id="phone-number-input"
          value={phone === undefined ? '' : phone}
          variant="outlined"
          placeholder="Phone number"
          error={phoneError !== undefined}
          onChange={phoneValidator(true)}
          onBlur={phoneValidator(false)}
        />
      </FormControl>

      <FormControl className={classes.control}>
        <Typography variant="subtitle2" gutterBottom>
          Location
        </Typography>
        <Typography variant="body2" gutterBottom>
          Providing your location allows us to respond to improve content relevance.
        </Typography>
        <LocationInput
          id="locations-input"
          onEnter={setLocation}
          value={location}
          style={{marginTop: '1rem'}}
        />
      </FormControl>

      <FormControl className={classes.control}>
        <Typography variant="subtitle2" gutterBottom>
          Date of birth
        </Typography>
        <Typography variant="body2" gutterBottom>
          Providing your date of birth allows us to improve content relevance.
        </Typography>
        <MuiPickersUtilsProvider utils={MomentUtils}>
          <DatePicker
            id="date-of-birth-input"
            inputVariant="outlined"
            value={dateOfBirth === null ? null : moment(dateOfBirth)}
            onChange={date=>setDateOfBirth(date.toISOString())}
            style={{marginTop: '1rem'}}
          />
        </MuiPickersUtilsProvider>
      </FormControl>

      <FormControl className={classes.control}>
        <Typography variant="subtitle2" gutterBottom>
          NDIS number
        </Typography>
        <Typography variant="body2" gutterBottom>
          Providing your NDIS number allows us to respond to service requests faster.
        </Typography>
        {ndisNumberError !== undefined ? (
          <Typography variant="body2" color="error" gutterBottom>
            {ndisNumberError}
          </Typography>
        ) : null}
        <TextInput
          id="ndis-number-input"
          value={ndisNumber === null ? '' : ndisNumber}
          variant="outlined"
          placeholder="NDIS number"
          error={ndisNumberError !== undefined}
          onChange={ndisNumberValidator(true)}
          onBlur={ndisNumberValidator(false)}
        />
      </FormControl>

      <FormControl className={classes.control}>
        <Typography variant="subtitle2" gutterBottom>
          NDIS plan type
        </Typography>
        <Typography variant="body2" gutterBottom>
          Providing your NDIS plan type allows us to improve content relevance.
        </Typography>
        <Select
          id="ndis-plan-type-input"
          value={ndisPlanType === null ? '' : ndisPlanType}
          variant="outlined"
          onChange={e => setNdisPlanType(e.target.value)}
          style={{marginTop: '1rem'}}
        >
          {planTypes.map(item => (<MenuItem key={item} value={item}>{item}</MenuItem>))}
        </Select>
      </FormControl>

      <FormControl className={classes.control}>
        <Typography variant="subtitle2" gutterBottom>
          NDIS plan start date
        </Typography>
        <Typography variant="body2" gutterBottom>
          Providing your NDIS plan start date allows us to personalise notifications and content.
        </Typography>
        <MuiPickersUtilsProvider utils={MomentUtils}>
          <DatePicker
            id="ndis-plan-start-input"
            inputVariant="outlined"
            value={ndisPlanStart === null ? null : moment(ndisPlanStart)}
            onChange={date=>setNdisPlanStart(date.toISOString())}
            style={{marginTop: '1rem'}}
          />
        </MuiPickersUtilsProvider>
      </FormControl>

      <div className={classes.row}>
        {errors ? (<AlertList alerts={[{severity: 'error', text: 'There are errors in one or more of your changes. Please check the fields and try again.'}]} onClose={removeAlert} />) : null}
        <AlertList alerts={alerts} onClose={removeAlert} />
      </div>

      <div className={classes.row}>
        <Button
          label="Save"
          color="primary"
          pointer="none"
          onClick={onSave}
          busy={busy}
          style={{width: '10rem'}}
        />
      </div>

    </div>
  );
};

ParticipantDetailsForm.propTypes = {
  isAdmin: PropTypes.bool,
  setProfile: PropTypes.func,
};

export default ParticipantDetailsForm;
